import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, BookOpen, HelpCircle, Clock, Award, Upload, X, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TRAINING_CATEGORIES = [
  'Safety',
  'Compliance',
  'Equipment',
  'Hazard Communication',
  'Emergency Response',
  'Personal Protective Equipment',
  'Site-Specific',
  'Other'
];

const TRAINING_TYPES = [
  { value: 'osha_standard', label: 'OSHA Standard', description: 'Transfers when worker moves companies' },
  { value: 'industry_standard', label: 'Industry Standard', description: 'Transfers when worker moves companies' },
  { value: 'company_private', label: 'Company Private', description: 'Does NOT transfer - stays with your company' }
];

const EXPIRY_OPTIONS = [
  { value: 1, label: '1 Year' },
  { value: 2, label: '2 Years' },
  { value: 3, label: '3 Years' },
  { value: 5, label: '5 Years' },
  { value: 0, label: 'Never Expires' }
];

const CompanyTrainingManager = () => {
  const { t } = useTranslation();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Safety',
    content: '',
    duration: 30,
    imageUrl: '',
    videoUrl: '',
    trainingType: 'company_private',
    certificateExpiryYears: 1,
    crafts: [],
    requiredForJSA: false,
    questions: []
  });
  
  // Quiz question being edited
  const [currentQuestion, setCurrentQuestion] = useState({
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0
  });

  useEffect(() => {
    loadTrainings();
  }, []);

  const loadTrainings = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/company/training');
      setTrainings(response.data);
    } catch (error) {
      console.error('Failed to load trainings:', error);
      toast.error('Failed to load training courses');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'Safety',
      content: '',
      duration: 30,
      imageUrl: '',
      videoUrl: '',
      trainingType: 'company_private',
      certificateExpiryYears: 1,
      crafts: [],
      requiredForJSA: false,
      questions: []
    });
    setCurrentQuestion({
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0
    });
    setEditingTraining(null);
  };

  const handleCreate = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const handleEdit = async (training) => {
    setEditingTraining(training);
    
    // Load quiz questions if they exist
    let questions = [];
    try {
      const quizResponse = await axiosInstance.get(`/company/training/${training.id}/quiz`);
      questions = quizResponse.data?.questions || [];
    } catch (error) {
      // Quiz may not exist
    }
    
    setFormData({
      title: training.title,
      description: training.description,
      category: training.category,
      content: training.content,
      duration: training.duration,
      imageUrl: training.imageUrl || '',
      videoUrl: training.videoUrl || '',
      trainingType: training.trainingType || 'company_private',
      certificateExpiryYears: training.certificateExpiryYears || 1,
      crafts: training.crafts || [],
      requiredForJSA: training.requiredForJSA || false,
      questions: questions
    });
    
    setShowCreateDialog(true);
  };

  const handleDelete = async (trainingId) => {
    if (!window.confirm('Are you sure you want to delete this training? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/company/training/${trainingId}`);
      toast.success('Training deleted successfully');
      loadTrainings();
    } catch (error) {
      toast.error('Failed to delete training');
    }
  };

  const addQuestion = () => {
    if (!currentQuestion.question.trim()) {
      toast.error('Please enter a question');
      return;
    }
    
    const filledOptions = currentQuestion.options.filter(opt => opt.trim());
    if (filledOptions.length < 2) {
      toast.error('Please provide at least 2 answer options');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, {
        question: currentQuestion.question,
        options: currentQuestion.options.filter(opt => opt.trim()),
        correctAnswer: currentQuestion.correctAnswer
      }]
    }));
    
    setCurrentQuestion({
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0
    });
  };

  const removeQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a training title');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    try {
      if (editingTraining) {
        await axiosInstance.put(`/company/training/${editingTraining.id}`, formData);
        toast.success('Training updated successfully');
      } else {
        await axiosInstance.post('/company/training', formData);
        toast.success('Training created successfully');
      }
      
      setShowCreateDialog(false);
      resetForm();
      loadTrainings();
    } catch (error) {
      console.error('Failed to save training:', error);
      toast.error(error.response?.data?.detail || 'Failed to save training');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              {t('trainingManager.title')}
            </CardTitle>
            <CardDescription>{t('trainingManager.subtitle')}</CardDescription>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-2" />
            {t('trainingManager.createTraining')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-gray-500">{t('trainingManager.loadingTrainings')}</div>
        ) : trainings.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">{t('trainingManager.noTrainings')}</p>
            <p className="text-sm">{t('trainingManager.createFirst')}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('trainingManager.trainingTitleCol')}</TableHead>
                <TableHead>{t('trainingManager.categoryCol')}</TableHead>
                <TableHead>{t('trainingManager.durationCol')}</TableHead>
                <TableHead>{t('trainingManager.quizCol')}</TableHead>
                <TableHead>{t('trainingManager.certExpiryCol')}</TableHead>
                <TableHead>{t('trainingManager.typeCol')}</TableHead>
                <TableHead>{t('trainingManager.actionsCol')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trainings.map((training) => (
                <TableRow key={training.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{training.title}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {training.description}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{training.category}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-gray-600">
                      <Clock className="w-4 h-4" />
                      {training.duration} min
                    </div>
                  </TableCell>
                  <TableCell>
                    {training.hasQuiz ? (
                      <Badge className="bg-green-100 text-green-700">
                        <HelpCircle className="w-3 h-3 mr-1" />
                        {training.questionCount} {t('trainingManager.questions')}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-500">{t('trainingManager.noQuiz')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Award className="w-4 h-4 text-amber-500" />
                      {training.certificateExpiryYears === 0 
                        ? 'Never' 
                        : `${training.certificateExpiryYears || 1} year${(training.certificateExpiryYears || 1) > 1 ? 's' : ''}`}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge 
                        className={
                          training.trainingType === 'company_private' 
                            ? 'bg-purple-100 text-purple-700' 
                            : training.trainingType === 'osha_standard'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-green-100 text-green-700'
                        }
                      >
                        {training.trainingType === 'company_private' ? t('trainingManager.privateType') : 
                         training.trainingType === 'osha_standard' ? t('trainingManager.oshaType') : t('trainingManager.industryType')}
                      </Badge>
                      {training.requiredForJSA && (
                        <Badge className="bg-orange-100 text-orange-700">
                          {t('trainingManager.jsaRequired')}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(training)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(training.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        if (!open) resetForm();
        setShowCreateDialog(open);
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTraining ? t('trainingManager.editTraining') : t('trainingManager.createNewTraining')}
            </DialogTitle>
            <DialogDescription>
              {editingTraining 
                ? t('trainingManager.editTrainingDesc')
                : t('trainingManager.createTrainingDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 border-b pb-2">{t('trainingManager.trainingDetails')}</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="title">{t('trainingManager.titleRequired')}</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t('trainingManager.titlePlaceholder')}
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="description">{t('trainingManager.descriptionRequired')}</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t('trainingManager.descriptionPlaceholder')}
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="category">{t('trainingManager.categoryLabel')}</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRAINING_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="duration">{t('trainingManager.durationMinutes')}</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 30 })}
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="content">{t('trainingManager.trainingContent')}</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder={t('trainingManager.contentPlaceholder')}
                    rows={5}
                  />
                </div>
                
                <div>
                  <Label htmlFor="videoUrl">{t('trainingManager.videoUrl')}</Label>
                  <Input
                    id="videoUrl"
                    value={formData.videoUrl || ''}
                    onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
                
                <div>
                  <Label htmlFor="imageUrl">{t('trainingManager.imageUrl')}</Label>
                  <Input
                    id="imageUrl"
                    value={formData.imageUrl || ''}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>

            {/* Certificate Settings */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 border-b pb-2">{t('trainingManager.certificateSettings')}</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="trainingType">{t('trainingManager.trainingType')}</Label>
                  <Select
                    value={formData.trainingType}
                    onValueChange={(value) => setFormData({ ...formData, trainingType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRAINING_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div>
                            <div className="font-medium">{type.label}</div>
                            <div className="text-xs text-gray-500">{type.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="expiry">{t('trainingManager.certificateExpiry')}</Label>
                  <Select
                    value={String(formData.certificateExpiryYears)}
                    onValueChange={(value) => setFormData({ ...formData, certificateExpiryYears: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPIRY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Required for JSA Toggle */}
              <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex-1">
                  <Label className="text-base font-semibold text-orange-900">{t('trainingManager.requiredForJsa')}</Label>
                  <p className="text-sm text-orange-700 mt-1">
                    {t('trainingManager.requiredForJsaDesc')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, requiredForJSA: !formData.requiredForJSA })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.requiredForJSA ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.requiredForJSA ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Quiz Questions */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 border-b pb-2">
                {t('trainingManager.quizQuestions')}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({t('trainingManager.quizPassNote')})
                </span>
              </h3>
              
              {/* Existing Questions */}
              {formData.questions.length > 0 && (
                <div className="space-y-2">
                  {formData.questions.map((q, index) => (
                    <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-sm">Q{index + 1}: {q.question}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {q.options.length} options • Correct: Option {q.correctAnswer + 1}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => removeQuestion(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add New Question */}
              <div className="border rounded-lg p-4 space-y-3 bg-blue-50/50">
                <div>
                  <Label>{t('trainingManager.questionLabel')}</Label>
                  <Input
                    value={currentQuestion.question}
                    onChange={(e) => setCurrentQuestion({ ...currentQuestion, question: e.target.value })}
                    placeholder={t('trainingManager.questionPlaceholder')}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>{t('trainingManager.answerOptions')}</Label>
                  {currentQuestion.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="correctAnswer"
                        checked={currentQuestion.correctAnswer === index}
                        onChange={() => setCurrentQuestion({ ...currentQuestion, correctAnswer: index })}
                        className="w-4 h-4 text-blue-600"
                      />
                      <Input
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...currentQuestion.options];
                          newOptions[index] = e.target.value;
                          setCurrentQuestion({ ...currentQuestion, options: newOptions });
                        }}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1"
                      />
                      {currentQuestion.correctAnswer === index && (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  ))}
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={addQuestion}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('trainingManager.addQuestion')}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit}>
              {editingTraining ? t('trainingManager.updateTraining') : t('trainingManager.createTraining')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CompanyTrainingManager;
